/* D6 (2026-09-06): generált számlakép a TÁROLT adatból (fejléc + tételsorok + ÁFA-bontás).
 *
 * Mérve: 1 112 bizonylatnak (83 %) nincs PDF-je; a NAV-forrásúak közül 95/1 207-nek van. A NAV
 * (és a Billingo) minden mezőt ad, amiből egy olvasható számlakép rajzolható — a felhasználó
 * saját bizonytalanságát csökkenti („mi volt ez a tétel?").
 *
 * ⚠⚠ NEM HITELES BIZONYLAT: vízjel + fejléc mondja ki; a könyvelői csomagba NEM kerül, a
 * teljesség-ellenőrzés hiányzó-PDF listáját NEM elégíti ki. Külön ablakban nyílik (saját
 * markup, a Layout nélkül), onnan nyomtatható / böngészőből PDF-be menthető.
 */
const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
const num = (v, d = 0) => (v == null || v === '' ? '' : Number(v).toLocaleString('hu-HU', { minimumFractionDigits: d, maximumFractionDigits: d }));
const money = (v, cur) => (v == null ? '' : `${num(v, (cur || 'HUF') === 'HUF' ? 0 : 2)} ${cur || 'HUF'}`);
const vatLabel = (r) => {
  if (!r) return '';
  if (r.vat_kind === 'PERCENTAGE' && r.vat_percentage != null) return `${num(r.vat_percentage, 0)}%`;
  const kind = { EXEMPTION: 'mentes', OUT_OF_SCOPE: 'ÁFA-körön kívüli', REVERSE_CHARGE: 'fordított adózás',
                 MARGIN_SCHEME: 'különbözeti', CONTENT: 'ÁFA-tartalom', AMOUNT_MISMATCH: 'eltérő adóalap', NO_VAT_CHARGE: 'nincs ÁFA' }[r.vat_kind] || r.vat_kind || '';
  return r.vat_case ? `${kind} (${r.vat_case})` : kind;
};

export function buildInvoiceImageHtml(doc, lines = [], vat = []) {
  const src = { NAV_API: 'NAV Online Számla', BILLINGO: 'Billingo', EMAIL_ATTACHMENT: 'e-mail (AI-olvasat)',
                EMAIL_LINK: 'e-mail link (AI-olvasat)', MANUAL: 'kézi feltöltés (AI-olvasat)' }[String(doc.source || '').toUpperCase()] || doc.source || '';
  const cur = doc.currency || 'HUF';
  const rows = (lines || []).map((l, i) => `
    <tr>
      <td class="c">${l.line_number ?? i + 1}</td>
      <td>${esc(l.description)}${l.product_code_value ? `<div class="dim">${esc(l.product_code_category || '')} ${esc(l.product_code_value)}</div>` : ''}</td>
      <td class="r">${num(l.quantity, 2)}</td>
      <td>${esc(l.unit_of_measure || '')}</td>
      <td class="r">${num(l.unit_price, 2)}</td>
      <td class="r">${num(l.net_amount, cur === 'HUF' ? 0 : 2)}</td>
      <td class="c">${esc(vatLabel(l))}</td>
      <td class="r">${num(l.vat_amount, cur === 'HUF' ? 0 : 2)}</td>
      <td class="r">${num(l.gross_amount, cur === 'HUF' ? 0 : 2)}</td>
    </tr>`).join('');
  const vatRows = (vat || []).map((r) => `
    <tr><td>${esc(vatLabel(r))}${r.vat_case_reason ? `<div class="dim">${esc(r.vat_case_reason)}</div>` : ''}</td>
      <td class="r">${money(r.net_amount, cur)}</td><td class="r">${money(r.vat_amount, cur)}</td><td class="r">${money(r.gross_amount, cur)}</td></tr>`).join('');
  const op = { STORNO: 'SZTORNÓ SZÁMLA', MODIFY: 'MÓDOSÍTÓ SZÁMLA' }[doc.nav_operation] || (doc.doc_type === 'proforma' ? 'DÍJBEKÉRŐ' : doc.doc_type === 'receipt' ? 'NYUGTA' : 'SZÁMLA');
  return `<!doctype html><html lang="hu"><head><meta charset="utf-8"><title>Számlakép ${esc(doc.invoice_number || doc.id)}</title>
<style>
  body{font-family:Segoe UI,Arial,sans-serif;color:#111;margin:0;padding:28px 36px;position:relative;background:#fff}
  .wm{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;pointer-events:none;z-index:0}
  .wm span{font-size:64px;font-weight:800;color:rgba(200,30,30,.10);transform:rotate(-24deg);white-space:nowrap;letter-spacing:.06em}
  .wrap{position:relative;z-index:1}
  .banner{border:2px solid #c81e1e;color:#c81e1e;padding:8px 12px;font-weight:700;font-size:13px;margin-bottom:18px;background:#fff5f5}
  h1{font-size:22px;margin:0 0 4px}
  .meta{display:grid;grid-template-columns:1fr 1fr;gap:18px;margin:14px 0 18px}
  .box{border:1px solid #ddd;border-radius:6px;padding:10px 12px;font-size:13px}
  .box h3{margin:0 0 6px;font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#666}
  .kv{display:grid;grid-template-columns:auto 1fr;gap:2px 10px;font-size:12.5px}
  .kv b{color:#444;font-weight:600}
  table{width:100%;border-collapse:collapse;font-size:12px;margin-top:6px}
  th{background:#f3f4f6;text-align:left;padding:6px 6px;border-bottom:1px solid #ccc;font-size:11px;text-transform:uppercase;letter-spacing:.04em}
  td{padding:5px 6px;border-bottom:1px solid #eee;vertical-align:top}
  td.r,th.r{text-align:right;font-variant-numeric:tabular-nums} td.c,th.c{text-align:center}
  .dim{color:#777;font-size:11px}
  .totals{margin-top:14px;display:flex;justify-content:flex-end}
  .totals table{width:auto;min-width:360px}
  .foot{margin-top:24px;font-size:11px;color:#777;border-top:1px solid #ddd;padding-top:8px}
  .print{position:fixed;top:10px;right:14px;z-index:2}
  .print button{font:inherit;padding:6px 12px;border:1px solid #888;border-radius:6px;background:#fff;cursor:pointer}
  @media print{.print{display:none}.wm span{color:rgba(200,30,30,.12)}}
</style></head><body>
<div class="wm"><span>NEM HITELES BIZONYLAT</span></div>
<div class="print"><button onclick="window.print()">Nyomtatás / PDF</button></div>
<div class="wrap">
  <div class="banner">NEM HITELES BIZONYLAT — a tárolt adatból (${esc(src)}) generált számlakép. Könyvelési célra az eredeti számla szükséges.</div>
  <h1>${esc(op)} ${esc(doc.invoice_number || '')}</h1>
  <div class="dim">puo · bizonylat #${esc(doc.id)} · forrás: ${esc(src)}${doc.original_invoice_number ? ` · eredeti: ${esc(doc.original_invoice_number)}` : ''}</div>
  <div class="meta">
    <div class="box"><h3>Kiállító</h3><div class="kv">
      <b>Név</b><span>${esc(doc.supplier_name)}</span>
      <b>Adószám</b><span>${esc(doc.supplier_tax_number)}</span>
      ${doc.supplier_address ? `<b>Cím</b><span>${esc(doc.supplier_address)}</span>` : ''}
      ${doc.bank_account_iban || doc.bank_account_hu ? `<b>Bankszámla</b><span>${esc(doc.bank_account_iban || doc.bank_account_hu)}</span>` : ''}
    </div></div>
    <div class="box"><h3>Vevő</h3><div class="kv">
      <b>Név</b><span>${esc(doc.customer_name)}</span>
      <b>Adószám</b><span>${esc(doc.customer_tax_number)}</span>
    </div></div>
    <div class="box"><h3>Dátumok</h3><div class="kv">
      <b>Kiállítás</b><span>${esc(doc.issue_date)}</span>
      <b>Teljesítés</b><span>${esc(doc.delivery_date)}</span>
      <b>Fizetési határidő</b><span>${esc(doc.due_date)}</span>
      ${doc.accounting_delivery_date ? `<b>Számviteli teljesítés</b><span>${esc(doc.accounting_delivery_date)}</span>` : ''}
      ${doc.billing_period_start ? `<b>Elszámolt időszak</b><span>${esc(doc.billing_period_start)} – ${esc(doc.billing_period_end)}</span>` : ''}
    </div></div>
    <div class="box"><h3>Fizetés</h3><div class="kv">
      <b>Mód</b><span>${esc(doc.payment_method || '')}</span>
      <b>Deviza</b><span>${esc(cur)}${doc.exchange_rate ? ` (árfolyam ${num(doc.exchange_rate, 4)})` : ''}</span>
      <b>Állapot</b><span>${esc(doc.status)}${doc.paid_date ? ` · kifizetve ${esc(doc.paid_date)}` : ''}</span>
    </div></div>
  </div>
  <table><thead><tr><th class="c">#</th><th>Megnevezés</th><th class="r">Menny.</th><th>Egys.</th><th class="r">Egységár</th><th class="r">Nettó</th><th class="c">ÁFA</th><th class="r">ÁFA össz.</th><th class="r">Bruttó</th></tr></thead>
  <tbody>${rows || '<tr><td colspan="9" class="dim">Nincs tárolt tételsor ehhez a bizonylathoz.</td></tr>'}</tbody></table>
  <div class="totals"><table>
    <thead><tr><th>ÁFA-kulcs</th><th class="r">Nettó</th><th class="r">ÁFA</th><th class="r">Bruttó</th></tr></thead>
    <tbody>${vatRows}
      <tr><td><b>Összesen</b></td><td class="r"><b>${money(doc.net_amount, cur)}</b></td><td class="r"><b>${money(doc.vat_amount, cur)}</b></td><td class="r"><b>${money(doc.gross_amount, cur)}</b></td></tr>
      ${doc.gross_amount_huf != null && cur !== 'HUF' ? `<tr><td class="dim" colspan="3">Bruttó HUF-ban</td><td class="r">${money(doc.gross_amount_huf, 'HUF')}</td></tr>` : ''}
    </tbody></table></div>
  <div class="foot">Generálta a puo ${new Date().toLocaleString('hu-HU')} · Ez a kép a rendszerben tárolt adatokból készült, nem az eredeti számla; adójogi bizonylatként nem használható.</div>
</div></body></html>`;
}

export function openInvoiceImage(doc, lines, vat) {
  const w = window.open('', '_blank');
  if (!w) return false;
  w.document.open();
  w.document.write(buildInvoiceImageHtml(doc, lines, vat));
  w.document.close();
  return true;
}
